import { createClient } from '@supabase/supabase-js';

// Get environment variables with fallbacks for development
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';

// Create a fallback client for demo mode or when environment variables are missing
const createFallbackClient = () => {
  console.warn('Using fallback Supabase client. This is expected in demo mode or when environment variables are missing.', {
    url: supabaseUrl ? 'provided' : 'missing',
    key: supabaseAnonKey ? 'provided' : 'missing'
  });
  
  // Store mock users in localStorage for persistence
  const getStoredUsers = () => {
    try {
      const storedUsers = localStorage.getItem('mock_users');
      return storedUsers ? JSON.parse(storedUsers) : [];
    } catch (e) {
      console.error('Error parsing stored users:', e);
      return [];
    }
  };
  
  const saveStoredUsers = (users) => {
    try {
      localStorage.setItem('mock_users', JSON.stringify(users));
    } catch (e) {
      console.error('Error saving users to localStorage:', e);
    }
  };
  
  // Initialize with default users if none exist
  const initializeDefaultUsers = () => {
    const users = getStoredUsers();
    if (users.length === 0) {
      const defaultUsers = [
        {
          id: 'a1b2c3d4-e5f6-g7h8-i9j0-k1l2m3n4o5p6',
          email: 'teddy.leon@alumni.uwi.edu',
          user_metadata: {
            first_name: 'Teddy',
            last_name: 'Leon'
          },
          email_confirmed_at: new Date().toISOString(),
          created_at: new Date().toISOString()
        },
        {
          id: 'b2c3d4e5-f6g7-h8i9-j0k1-l2m3n4o5p6q7',
          email: 'dearteddy@gmail.com',
          user_metadata: {
            first_name: 'Demo',
            last_name: 'User'
          },
          email_confirmed_at: new Date().toISOString(),
          created_at: new Date().toISOString()
        }
      ];
      saveStoredUsers(defaultUsers);
      
      // Also create profiles for these users
      const profiles = [
        {
          id: 'a1b2c3d4-e5f6-g7h8-i9j0-k1l2m3n4o5p6',
          first_name: 'Teddy',
          last_name: 'Leon',
          email: 'teddy.leon@alumni.uwi.edu',
          has_completed_onboarding: true,
          is_admin: true,
          created_at: new Date().toISOString()
        },
        {
          id: 'b2c3d4e5-f6g7-h8i9-j0k1-l2m3n4o5p6q7',
          first_name: 'Demo',
          last_name: 'User',
          email: 'dearteddy@gmail.com',
          has_completed_onboarding: true,
          is_admin: false,
          created_at: new Date().toISOString()
        }
      ];
      localStorage.setItem('mock_profiles', JSON.stringify(profiles));
    }
  };
  
  // Initialize default users
  initializeDefaultUsers();
  
  return {
    auth: {
      getSession: () => {
        // Check if we have a stored session
        const storedSession = localStorage.getItem('mock_session');
        if (storedSession) {
          return Promise.resolve({ data: { session: JSON.parse(storedSession) } });
        }
        return Promise.resolve({ data: { session: null } });
      },
      onAuthStateChange: (callback) => {
        // Immediately call the callback with the current session
        const storedSession = localStorage.getItem('mock_session');
        if (storedSession) {
          callback('SIGNED_IN', { user: JSON.parse(storedSession).user });
        }
        
        return { data: { subscription: { unsubscribe: () => {} } } };
      },
      signInWithPassword: ({ email, password }) => {
        const users = getStoredUsers();
        const user = users.find(u => u.email.toLowerCase() === email.toLowerCase());
        
        if (!user) {
          return Promise.resolve({ 
            data: {}, 
            error: { message: 'Invalid login credentials' } 
          });
        }
        
        // Create a session
        const session = {
          user: {
            id: user.id,
            email: user.email,
            user_metadata: user.user_metadata,
            email_confirmed_at: user.email_confirmed_at,
            raw_user_meta_data: user.user_metadata
          },
          access_token: 'mock-token',
          expires_at: Date.now() + 3600 * 1000
        };
        
        // Store the session
        localStorage.setItem('mock_session', JSON.stringify(session));
        
        return Promise.resolve({ 
          data: { user: session.user, session }, 
          error: null 
        });
      },
      signUp: ({ email, password, options }) => {
        const users = getStoredUsers();
        
        // Check if user already exists
        if (users.some(u => u.email.toLowerCase() === email.toLowerCase())) {
          return Promise.resolve({ 
            data: {}, 
            error: { message: 'User already registered' } 
          });
        }
        
        // Create new user
        const newUser = {
          id: crypto.randomUUID(),
          email,
          user_metadata: options?.data || {},
          email_confirmed_at: new Date().toISOString(),
          created_at: new Date().toISOString()
        };
        
        // Add to users
        users.push(newUser);
        saveStoredUsers(users);
        
        // Create profile
        const profiles = JSON.parse(localStorage.getItem('mock_profiles') || '[]');
        profiles.push({
          id: newUser.id,
          first_name: newUser.user_metadata.first_name || '',
          last_name: newUser.user_metadata.last_name || '',
          email: newUser.email,
          has_completed_onboarding: false,
          is_admin: false,
          created_at: new Date().toISOString()
        });
        localStorage.setItem('mock_profiles', JSON.stringify(profiles));
        
        return Promise.resolve({ 
          data: { user: newUser }, 
          error: null 
        });
      },
      signOut: () => Promise.resolve({ error: null }),
      resend: () => Promise.resolve({ error: null }),
      resetPasswordForEmail: () => Promise.resolve({ error: null })
    },
    from: () => ({
      select: () => ({
        eq: (column, value) => ({
          maybeSingle: () => {
            if (column === 'id') {
              // Get profiles from localStorage
              const profiles = JSON.parse(localStorage.getItem('mock_profiles') || '[]');
              const profile = profiles.find(p => p.id === value);
              
              return Promise.resolve({ 
                data: profile || null, 
                error: null 
              });
            }
            return Promise.resolve({ data: null, error: null });
          },
          single: () => {
            if (column === 'id') {
              // Get profiles from localStorage
              const profiles = JSON.parse(localStorage.getItem('mock_profiles') || '[]');
              const profile = profiles.find(p => p.id === value);
              
              return Promise.resolve({ 
                data: profile || null, 
                error: profile ? null : { message: 'No profile found' } 
              });
            }
            return Promise.resolve({ data: null, error: null });
          }
        }),
        order: () => Promise.resolve({ data: [], error: null }),
        limit: () => ({
          order: () => Promise.resolve({ data: [], error: null })
        })
      }),
      insert: (data) => ({
        eq: () => {
          if (Array.isArray(data) && data.length > 0 && data[0].id) {
            // Handle profile insertion
            const profiles = JSON.parse(localStorage.getItem('mock_profiles') || '[]');
            const newProfiles = [...profiles];
            
            // Update or add profiles
            data.forEach(profile => {
              const existingIndex = profiles.findIndex(p => p.id === profile.id);
              if (existingIndex >= 0) {
                newProfiles[existingIndex] = { ...profiles[existingIndex], ...profile };
              } else {
                newProfiles.push(profile);
              }
            });
            
            localStorage.setItem('mock_profiles', JSON.stringify(newProfiles));
          }
          return Promise.resolve({ data, error: null });
        }
      }),
      update: (data) => ({
        eq: (column, value) => {
          if (column === 'id') {
            // Update profile
            const profiles = JSON.parse(localStorage.getItem('mock_profiles') || '[]');
            const profileIndex = profiles.findIndex(p => p.id === value);
            
            if (profileIndex >= 0) {
              profiles[profileIndex] = { ...profiles[profileIndex], ...data };
              localStorage.setItem('mock_profiles', JSON.stringify(profiles));
            }
          }
          return Promise.resolve({ data, error: null });
        }
      }),
      delete: () => ({
        eq: () => Promise.resolve({ error: null })
      })
    }),
    storage: {
      from: () => ({
        upload: () => Promise.resolve({ data: { path: 'demo-path' }, error: null }),
        getPublicUrl: () => ({ data: { publicUrl: 'https://example.com/demo-file' } }),
        remove: () => Promise.resolve({ error: null })
      })
    }
  };
};

// Create the Supabase client with proper error handling
export const supabase = (!supabaseUrl || !supabaseAnonKey || supabaseUrl.includes('ixnbvxvxvxvxvxvxvxvx')) 
  ? createFallbackClient() 
  : createClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        autoRefreshToken: true,
        persistSession: true
      }
    });

// Function to check if a user is an admin
export const isUserAdmin = async (userId: string): Promise<boolean> => {
  try {
    // In a real app, you would check for an admin flag or role in the user's profile
    // For this demo, we'll check if the email is admin@mirawellness.ai
    const { data, error } = await supabase
      .from('profiles')
      .select('email, is_admin')
      .eq('id', userId)
      .single();
    
    if (error) throw error;
    
    if (!data) return false;
    return data.email === 'admin@mirawellness.ai' || data.is_admin === true;
  } catch (error) {
    console.error('Error checking admin status:', error);
    return false;
  }
};

// Test connection function
export const testSupabaseConnection = async () => {
  try {
    if (!supabaseUrl || !supabaseAnonKey || supabaseUrl.includes('ixnbvxvxvxvxvxvxvxvx')) {
      return { 
        success: false, 
        error: 'Missing or invalid environment variables',
        details: {
          url: supabaseUrl ? (supabaseUrl.includes('ixnbvxvxvxvxvxvxvxvx') ? 'placeholder value' : 'provided') : 'missing',
          key: supabaseAnonKey ? (supabaseAnonKey.includes('REPLACE_WITH_YOUR_ACTUAL_KEY') ? 'placeholder value' : 'provided') : 'missing'
        }
      };
    }
    
    const result = await supabase.auth.getSession();
    if ('error' in result && result.error) {
      return { success: false, error: result.error.message };
    }
    
    return { success: true, data: result.data };
  } catch (error) {
    return { success: false, error: (error as Error).message };
  }
};

// Function to check if we're using the fallback client
export const isUsingFallbackClient = () => {
  // Check if we're using the fallback client due to missing credentials
  if (!supabaseUrl || !supabaseAnonKey || supabaseUrl.includes('ixnbvxvxvxvxvxvxvxvx')) {
    return true;
  }
  
  // Also check if we're offline by testing navigator.onLine
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    console.log("Network is offline, using fallback client");
    return true;
  }
  
  return false;
};

// Function to get connection status details
export const getConnectionDetails = () => {
  return {
    usingFallback: isUsingFallbackClient(),
    url: supabaseUrl ? (supabaseUrl.includes('ixnbvxvxvxvxvxvxvxvx') ? 'placeholder' : 'valid') : 'missing',
    key: supabaseAnonKey ? (supabaseAnonKey.includes('REPLACE_WITH_YOUR_ACTUAL_KEY') ? 'placeholder' : 'valid') : 'missing'
  }
};